export type CalendarConnectionOption = {
  value: string;
  label: string;
};

export type CalendarIntegrationCapabilityState =
  | 'disabled_by_policy'
  | 'connected';

export type CalendarIntegrationProviderState =
  | 'disabled_by_policy'
  | 'enabled_provider_unavailable'
  | 'enabled_user_action_required'
  | 'connected'
  | 'degraded'
  | 'revoked';

export type CalendarIntegrationCapabilityRecord = {
  state: CalendarIntegrationCapabilityState;
  allowed: boolean;
  requires_user_opt_in: boolean;
  requires_connected_provider: boolean;
  requires_published_schedule: boolean;
  data_profile: string;
  reason: string;
};

export type CalendarIntegrationProviderRecord = {
  provider: string;
  label: string;
  allowed: boolean;
  oauth_ready: boolean;
  auth_identity_linked: boolean;
  connection_state: CalendarIntegrationProviderState;
  busy_time_import_state: CalendarIntegrationProviderState;
  schedule_delivery_state: CalendarIntegrationProviderState;
  schedule_delivery_mode: string;
  provider_notes: string | null;
  sync_preference: string;
  notify_when_ready: boolean;
  sync_preference_options: CalendarConnectionOption[];
  connected_email: string | null;
  connected_at: string | null;
  connect_url: string | null;
  disconnect_url: string | null;
};

export type CalendarIntegrationResponse = {
  resolved_scope: {
    company_id: number;
    scope_type: string;
    scope_id: number | null;
    policy_id: number | null;
    policy_version: number | null;
    is_synthetic_deny_all: boolean;
  };
  selected_group: {
    id: number;
    slug: string;
    name: string;
  };
  selected_company: {
    id: number;
    slug: string;
    name: string;
  };
  selected_service_line: {
    id: number;
    name: string;
    code: string | null;
  } | null;
  selected_schedule_type: {
    id: number;
    name: string;
    code: string | null;
  } | null;
  policy: {
    status: string;
    default_export_mode: string;
    default_provider_mode: string;
    missing_provider_behavior: string;
    matched_policies: Array<{
      scope_type: string;
      scope_id: number | null;
      policy_id: number;
      policy_version: number;
      status: string;
    }>;
  };
  capabilities: {
    schedule_export_download: CalendarIntegrationCapabilityRecord;
    schedule_subscribe_feed: CalendarIntegrationCapabilityRecord;
    provider_busy_time_import: CalendarIntegrationCapabilityRecord;
    provider_schedule_delivery: CalendarIntegrationCapabilityRecord;
    connected_calendar_status_display: CalendarIntegrationCapabilityRecord;
  };
  providers: CalendarIntegrationProviderRecord[];
  data_profile: {
    profile_key: string;
    redaction_label: string;
    includes: {
      title: boolean;
      location: boolean;
      role_or_service_label: boolean;
      request_reason: boolean;
      admin_annotations: boolean;
      private_comments: boolean;
      contact_details: boolean;
      internal_notes: boolean;
    };
  };
  lifecycle: {
    feed_rotation_days: number;
    revoke_feed_on_policy_disable: boolean;
    revoke_tokens_on_provider_disable: boolean;
    retry_backoff_minutes: number;
    max_degraded_days_before_user_notice: number;
    audit_retention_days: number;
  };
  user_choices: Record<string, {
    sync_preference: string;
    notify_when_ready: boolean;
  }>;
};

const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

const readJson = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const message = response.status === 422
      ? 'Please check the integration settings and try again.'
      : `Calendar integration request failed (${response.status}).`;
    throw new Error(message);
  }

  return response.json() as Promise<T>;
};

export const fetchCalendarIntegrations = async (): Promise<CalendarIntegrationResponse> =>
  readJson<CalendarIntegrationResponse>(
    await fetch('/api/v1/secure/my-schedule/calendar-integrations', {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })
  );

export const saveCalendarConnectionPreference = async (payload: {
  provider: string;
  sync_preference: string;
  notify_when_ready: boolean;
}): Promise<void> => {
  await readJson(
    await fetch('/api/v1/secure/profile/calendar-connections', {
      method: 'PUT',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken,
      },
      body: JSON.stringify(payload),
    })
  );
};

export const deleteCalendarConnection = async (provider: string): Promise<void> => {
  await readJson(
    await fetch(`/api/v1/secure/profile/calendar-connections/${provider}`, {
      method: 'DELETE',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-CSRF-TOKEN': csrfToken,
      },
    })
  );
};
